import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { BIBLE_TRANSLATIONS, type BibleTranslation, type ScriptureReference } from '@flock-types/chat.types';
import useScriptureLookup from '@/hooks/useScriptureLookup';

interface Props {
  onSendNow: (scripture: ScriptureReference) => void;
  onInsert: (scripture: ScriptureReference) => void;
  onClose: () => void;
}

/** Bible verse lookup popover — send as a card or attach to the composer. */
export default function ScripturePickerPanel({ onSendNow, onInsert, onClose }: Props) {
  const [reference, setReference] = useState('');
  const { lookup, loading, error, result, translation, setTranslation, clearResult } =
    useScriptureLookup();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 z-30 mb-2 w-96 rounded-xl border border-flock-gray-200 bg-white p-3 shadow-2xl"
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-flock-navy-800">Share Scripture</span>
        <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          lookup(reference);
        }}
        className="flex gap-2"
      >
        <input
          autoFocus
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. John 3:16 or Psalm 23:1-3"
          className="min-w-0 flex-1 rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
        />
        <select
          value={translation}
          onChange={(e) => {
            setTranslation(e.target.value as BibleTranslation);
            clearResult();
          }}
          className="rounded-lg border border-flock-gray-200 bg-white px-2 py-2 text-sm"
        >
          {BIBLE_TRANSLATIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={loading || !reference.trim()}
          className="rounded-lg bg-flock-navy-600 px-3 py-2 text-sm font-medium text-white hover:bg-flock-navy-700 disabled:opacity-50"
        >
          {loading ? '…' : 'Look up'}
        </button>
      </form>

      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {result && (
        <div className="mt-3">
          <div className="rounded-lg border-l-4 border-flock-navy-400 bg-flock-navy-50 px-3 py-2 text-sm text-flock-navy-800">
            <div className="mb-0.5 font-semibold">
              {result.reference} ({result.translation})
            </div>
            <div className="max-h-32 overflow-y-auto italic">“{result.text}”</div>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => onSendNow(result)}
              className="flex-1 rounded-lg bg-flock-navy-600 px-3 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
            >
              Send Now
            </button>
            <button
              onClick={() => onInsert(result)}
              className="flex-1 rounded-lg border border-flock-gray-300 px-3 py-2 text-sm font-medium text-flock-navy-700 hover:bg-flock-navy-50"
            >
              Add a Message
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
